import React, { useMemo, useState } from "react";

import { useGetDatasetCategories } from "api/api-hooks/datasets-hooks";
import {
  IScheduledTaskPropertyRef,
  InputChangeEvent,
} from "api/model/airflow-task/task-template/TaskTemplateInfo";
import DatasetCategoryDropdown from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowEditCompleteEvent, DataTableRowEditEvent } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";

interface ITaskConfigCategoryTableProps extends IScheduledTaskPropertyRef {
  header: string;
  categories: Record<string, number>;
  onChange: (e: InputChangeEvent<Record<string, number>>) => void;
  bodyClass?: string;
}

interface ICategoryRowModel {
  key: string;
  category: { value: number | null };
  id: string;
}

export default function TaskConfigCategoryTable(props: ITaskConfigCategoryTableProps) {
  const { data, isLoading } = useGetDatasetCategories();

  const titleMap = useMemo(() => {
    const newTitleMap: Record<number, string> = {};
    if (data) {
      data.forEach(it => {
        newTitleMap[it.id!!] = it.toString();
      });
    }
    return newTitleMap;
  }, [data]);

  if (isLoading) return <i className="pi pi-spin pi-spinner" />;
  return <TaskConfigCategoryTableWrapped {...props} titleMap={titleMap} />;
}

function TaskConfigCategoryTableWrapped(
  props: ITaskConfigCategoryTableProps & { titleMap: Record<number, string> }
) {
  const [dataArr, setDataArr] = useState<ICategoryRowModel[]>(mapToList(props.categories));
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});

  const propagateChangesStateUp = (arr: ICategoryRowModel[]) => {
    const c: Record<string, number> = {};

    arr.forEach(e => {
      if (e.category?.value !== null && e.category?.value !== undefined) {
        c[e.key] = e.category.value; // Category id must be defined
      }
    });

    props.onChange({ value: c, property: props.property });
  };

  const confirmWrapper = (event: any, confirmHandler: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.are-you-really-want-to-delete-this-category-mapping"),
      icon: "pi pi-exclamation-triangle",
      accept: confirmHandler,
    });
  };

  const showWarn = () => {
    easasAlert({
      summary: T("alert.invalid"),
      detail: T("alert.category-must-be-selected"),
      severity: "warn",
      life: 3000,
    });
  };

  const handleAdd = () => {
    const newRow = { key: "new_category", category: { value: null }, id: v4() } as ICategoryRowModel;
    setDataArr(prev => [...prev, newRow]);
    setEditingRows(prev => ({ ...prev, [newRow.id]: true }));
  };

  const handleDelete = (row: ICategoryRowModel) => {
    const filtered = [...dataArr.filter(item => item.id !== row.id)];
    setDataArr(prev => prev.filter(item => item.id !== row.id));
    propagateChangesStateUp(filtered);
  };

  const headerTemplate = (
    <div className="flex justify-content-between">
      <h4>{props.header}</h4>
      <Button icon="pi pi-plus" className="p-button-success" onClick={handleAdd} />
    </div>
  );

  const keyEditorTemplate = (opt: ColumnEditorOptions) => {
    return (
      <InputText
        placeholder={T("word.key")}
        value={opt.rowData?.key || ""}
        onChange={e => opt.editorCallback?.(e.target.value)}
      />
    );
  };

  const valueEditorTemplate = (opt: ColumnEditorOptions) => {
    return (
      <DatasetCategoryDropdown value={opt.rowData.category?.value} onChange={e => opt.editorCallback?.(e)} />
    );
  };

  const valueBodyTemplate = (row: ICategoryRowModel) => {
    return <span>{props.titleMap[row.category?.value!!]}</span>;
  };

  const renderDeleteButton = (row: ICategoryRowModel) => {
    return (
      <Button
        icon="pi pi-trash"
        className="const-del-btn  p-button-rounded p-button-text p-button-danger"
        onClick={e => confirmWrapper(e, () => handleDelete(row))}
      />
    );
  };

  const handleRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    const newData = e.newData as ICategoryRowModel;
    setDataArr(prev => {
      const updated = prev.map((item, index) => (index === e.index ? newData : item));
      propagateChangesStateUp(updated);
      return updated;
    });
  };

  const onRowEditChange = (e: DataTableRowEditEvent) => {
    const newData = dataArr.at(e.index);
    if (newData?.category?.value === null) {
      const exist = e.data[newData!.id];
      if (!exist) {
        showWarn();
        return;
      }
    }
    setEditingRows(e.data);
  };

  const handleRowEditCancel = (e: DataTableRowEditEvent) => {
    const data = e.data as ICategoryRowModel;
    if (data.category?.value === null) {
      setDataArr(prev => prev.filter(item => item.id !== data.id));
      setEditingRows(prev => ({ ...prev, [data.id]: false }));
    }
  };

  return (
    <DataTable
      value={dataArr}
      editMode="row"
      onRowEditComplete={handleRowEditComplete}
      onRowEditChange={onRowEditChange}
      onRowEditCancel={handleRowEditCancel}
      editingRows={editingRows}
      dataKey="id"
      header={headerTemplate}
      width="100%"
      className={props.bodyClass}
      size="small"
    >
      <Column field="key" header={T("word.key")} editor={keyEditorTemplate} style={{ width: "20rem" }} />
      <Column
        field="category.value"
        header={T("word.value")}
        editor={valueEditorTemplate}
        body={valueBodyTemplate}
      />
      <Column rowEditor align="right" className="w-5rem p-0" />
      <Column body={renderDeleteButton} className="w-3rem" />
    </DataTable>
  );
}

function mapToList(map: Record<string, number>): ICategoryRowModel[] {
  return Object.keys(map).map(k => {
    return { key: k, category: { value: map[k] }, id: v4() } as ICategoryRowModel;
  });
}
